// client/src/components/doctors/dashboard/DoctorDashboard.jsx - COMPLETE WITH STATUS GUARD INTEGRATION
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureControl } from '../../hooks/useFeatureControl';
import { useTenant } from '../../context/TenantContext';
import FeatureWrapper from '../common/FeatureWrapper';

// ✅ NEW: Import DoctorStatusGuard components
import DoctorStatusGuard, { StatusBanner, RestrictedFeatureMessage } from '../common/DoctorStatusGuard';
import '../../styles/components/doctor/DoctorStatus.css';

import doctorService from '../../services/doctorService';
import '../../styles/components/doctor/DoctorDashboard.css';

const DoctorDashboard = () => {
  // ✅ Feature control and tenant context
  const featureControl = useFeatureControl();
  const { currentTenant, getThemeStyles, platformName } = useTenant();
  const theme = getThemeStyles();
  
  // ✅ Existing state
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalAppointments: 0,
    pendingEntries: 0,
    newMessages: 0
  });
  const [, setRecentEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('This Week');
  const [doctorInfo, setDoctorInfo] = useState({
    firstName: '',
    lastName: ''
  });
  
  // ✅ NEW: Profile picture state (same as layout)
  const [currentUser, setCurrentUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // ✅ NEW: User state for DoctorStatusGuard
  const [user, setUser] = useState(null);
  
  // ✅ ENHANCED: Changed to store ALL appointments, not just today's
  const [allAppointments, setAllAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  
  // ✅ NEW: Slider state for appointments
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // ✅ NEW: Modal state for appointment details
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  
  // ✅ Calendar state
  const [calendarState, setCalendarState] = useState({
    currentDate: new Date(),
    viewMode: 'week',
    selectedDate: new Date()
  });
  
  const navigate = useNavigate();

  // ✅ NEW: Enhanced profile loading function with user state for StatusGuard
  const loadDoctorInfo = async () => {
    try {
      setProfileLoading(true);
      
      // First, get user data from storage (like the layout does)
      const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userData) {
        const userObj = JSON.parse(userData);
        setCurrentUser(userObj);
        setUser(userObj); // ✅ NEW: Set user for StatusGuard
        
        // Update doctorInfo state with initial data
        setDoctorInfo({
          firstName: userObj.firstName || 'Doctor',
          lastName: userObj.lastName || ''
        });
      }
      
      // Then fetch fresh profile data from API
      try {
        const response = await doctorService.getProfile();
        let profileData = {};
        
        if (response.success && response.data) {
          profileData = response.data;
        } else if (response.data) {
          profileData = response.data;
        } else {
          profileData = response;
        }
        
        // Update both currentUser and doctorInfo states
        const updatedUser = {
          ...currentUser,
          ...profileData,
          id: currentUser?.id || currentUser?._id || profileData._id,
          role: currentUser?.role || profileData.role || 'doctor'
        };
        
        setCurrentUser(updatedUser);
        setUser(updatedUser); // ✅ NEW: Update user for StatusGuard
        
        setDoctorInfo({
          firstName: profileData.firstName || 'Doctor',
          lastName: profileData.lastName || ''
        });
        
        console.log('👨‍⚕️ [Dashboard Profile] Doctor info loaded:', profileData.firstName);
        console.log('🔒 [StatusGuard] User status:', {
          accountStatus: updatedUser.accountStatus,
          verificationStatus: updatedUser.verificationStatus,
          role: updatedUser.role
        });
        
      } catch (apiError) {
        console.warn('⚠️ [Dashboard Profile] Could not fetch fresh profile data:', apiError.message);
        // Keep the data from localStorage if API fails
      }
      
    } catch (error) {
      console.error('❌ [Dashboard Profile] Error loading doctor info:', error);
      // Fallback values
      setDoctorInfo({ firstName: 'Doctor', lastName: '' });
    } finally {
      setProfileLoading(false);
    }
  };

  // ✅ NEW: Profile picture display helper (same as layout)
  const getDoctorProfileDisplay = () => {
    if (profileLoading) {
      return (
        <div className="doctor-profile-loading">
          <div className="profile-loading-spinner"></div>
        </div>
      );
    }
    
    if (currentUser?.profilePicture) {
      return (
        <>
          <img 
            src={currentUser.profilePicture} 
            alt="Profile" 
            className="doctor-profile-image"
            onError={(e) => {
              console.warn('❌ [Dashboard Profile] Image failed to load:', e.target.src);
              e.target.style.display = 'none';
              const initialsDiv = e.target.parentNode.querySelector('.doctor-profile-initials-fallback');
              if (initialsDiv) {
                initialsDiv.style.display = 'flex';
              }
            }}
            onLoad={() => {
              console.log('✅ [Dashboard Profile] Image loaded successfully');
            }}
          />
          <div className="doctor-profile-initials-fallback" style={{ display: 'none' }}>
            {(currentUser?.firstName || 'D').charAt(0)}{(currentUser?.lastName || 'R').charAt(0)}
          </div>
        </>
      );
    } else {
      const firstName = currentUser?.firstName || doctorInfo.firstName || 'D';
      const lastName = currentUser?.lastName || doctorInfo.lastName || 'R';
      return (
        <div className="doctor-profile-initials">
          {firstName.charAt(0)}{lastName.charAt(0)}
        </div>
      );
    }
  };

  // ✅ NEW: Slider navigation functions
  const goToPrevious = () => {
    setCurrentSlide(prev => 
      prev === 0 ? allAppointments.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentSlide(prev => 
      prev === allAppointments.length - 1 ? 0 : prev + 1
    );
  };

  // ✅ NEW: Reset slider when appointments change
  useEffect(() => {
    setCurrentSlide(0);
  }, [allAppointments.length]);

  // ✅ NEW: Modal handler functions
  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const closeAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
  };

  // ✅ Enhanced function to handle joining video meeting with feature check
  const handleJoinMeeting = (appointment) => {
    // Check if Care/Report feature is enabled (appointments functionality)
    if (!featureControl.isFeatureEnabled('Care / Report')) {
      alert('Video meeting functionality is currently disabled for your clinic. Please contact your administrator.');
      return;
    }

    const meetingLink = appointment.meetingLink;
    
    if (!meetingLink) {
      alert('No meeting link available for this appointment. Please ensure the appointment is confirmed and has a video session scheduled.');
      return;
    }
    
    console.log('🎥 Doctor joining meeting:', meetingLink);
    
    // Determine meeting platform
    const getMeetingPlatform = (url) => {
      try {
        const urlObj = new URL(url);
        if (urlObj.hostname === 'meet.google.com') return 'Google Meet';
        if (urlObj.hostname === 'meet.jit.si') return 'Jitsi Meet';
        if (urlObj.hostname.includes('zoom')) return 'Zoom';
        return 'Video Conference';
      } catch (e) {
        return 'Video Conference';
      }
    };
    
    const platform = getMeetingPlatform(meetingLink);
    
    // Show confirmation dialog with tenant branding
    const confirmed = window.confirm(
      `Join ${platform} session with ${appointment.patientName}?\n\n` +
      `${platformName} - ${currentTenant?.name || 'Clinic'}\n` +
      `Session Platform: ${platform}\n` +
      `Patient: ${appointment.patientName}\n` +
      `Time: ${appointment.time}\n\n` +
      `This will open the video conference in a new window.`
    );
    
    if (confirmed) {
      // Open meeting in new window/tab
      window.open(meetingLink, '_blank', 'noopener,noreferrer');
      
      // Show success message with tenant branding
      setTimeout(() => {
        alert(
          `${platform} should now be opening in a new window.\n\n` +
          `📋 ${platformName} Session Tips:\n` +
          `• Ensure good internet connection\n` +
          `• Test your camera and microphone\n` +
          `• Both you and the patient will join the same room\n` +
          `• No account registration required for Jitsi Meet`
        );
      }, 1000);
    }
  };

  // ✅ Keep existing functions unchanged
  const isSessionActive = (appointment) => {
    if (!appointment.appointmentDate) return false;
    
    const appointmentTime = new Date(appointment.appointmentDate);
    const now = new Date();
    const diffMinutes = (appointmentTime.getTime() - now.getTime()) / (1000 * 60);
    
    return diffMinutes <= 15 && diffMinutes >= -120;
  };

  const getSessionStatus = (appointment) => {
    if (!appointment.meetingLink) return { status: 'No Meeting', color: '#666' };
    if (!appointment.appointmentDate) return { status: 'Scheduled', color: theme?.primaryColor || '#4CAF50' };
    
    const appointmentTime = new Date(appointment.appointmentDate);
    const now = new Date();
    const diffMinutes = (appointmentTime.getTime() - now.getTime()) / (1000 * 60);
    
    if (diffMinutes <= 5 && diffMinutes >= -60) {
      return { status: 'Active Now', color: '#FF4444' };
    } else if (diffMinutes <= 15 && diffMinutes > 5) {
      return { status: 'Starting Soon', color: '#FF9800' };
    } else if (diffMinutes > 15) {
      return { status: 'Scheduled', color: theme?.primaryColor || '#4CAF50' };
    } else {
      return { status: 'Ended', color: '#666' };
    }
  };

  // ✅ ENHANCED: Process ALL appointments and organize by date priority
  const processAllAppointments = (allAppointments) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    console.log('🔍 Processing ALL appointments:', allAppointments.length);
    
    // Transform all appointments to consistent format
    const processedAppointments = allAppointments.map(appt => ({
      id: appt._id,
      patientName: appt.patient ? 
        `${appt.patient.firstName || ''} ${appt.patient.lastName || ''}`.trim() :
        'Unknown Patient',
      patientType: "Patient",
      appointmentDate: appt.appointmentDate,
      time: appt.appointmentDate ? new Date(appt.appointmentDate).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      }) : 'Time TBD',
      date: appt.appointmentDate ? new Date(appt.appointmentDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }) : 'Date TBD',
      meetingLink: appt.meetingLink,
      status: appt.status,
      meetingType: appt.meetingType || 'jitsi',
      duration: appt.duration || 30,
      appointmentType: appt.appointmentType || 'Consultation',
      rawDate: appt.appointmentDate ? new Date(appt.appointmentDate) : new Date(),
      isToday: appt.appointmentDate ? new Date(appt.appointmentDate).toISOString().split('T')[0] === todayStr : false,
      isPast: appt.appointmentDate ? new Date(appt.appointmentDate) < today : false,
      isFuture: appt.appointmentDate ? new Date(appt.appointmentDate) > today : true
    }));

    // ✅ NEW: Separate today's appointments
    const todaysAppts = processedAppointments.filter(appt => appt.isToday);
    
    // ✅ NEW: Sort all appointments: Today first, then by date
    const sortedAppointments = processedAppointments.sort((a, b) => {
      // Today's appointments always come first
      if (a.isToday && !b.isToday) return -1;
      if (!a.isToday && b.isToday) return 1;
      
      // Within same day category, sort by actual time
      return a.rawDate.getTime() - b.rawDate.getTime();
    });

    console.log('✅ Processed appointments breakdown:');
    console.log(`   - Today's appointments: ${todaysAppts.length}`);
    console.log(`   - Total appointments: ${sortedAppointments.length}`);
    console.log(`   - Future appointments: ${processedAppointments.filter(a => a.isFuture && !a.isToday).length}`);
    console.log(`   - Past appointments: ${processedAppointments.filter(a => a.isPast).length}`);
    
    return { allSorted: sortedAppointments, todaysOnly: todaysAppts };
  };

  // ✅ ENHANCED: Helper function to get appointment status badge
  const getAppointmentStatusBadge = (appointment) => {
    if (appointment.isToday) {
      if (isSessionActive(appointment)) {
        return { text: 'Active Now', className: 'status-active' };
      } else {
        return { text: 'Today', className: 'status-today' };
      }
    } else if (appointment.isPast) {
      return { text: 'Past', className: 'status-past' };
    } else {
      return { text: 'Upcoming', className: 'status-upcoming' };
    }
  };

  // ✅ ENHANCED: useEffect with proper profile picture integration
  useEffect(() => {
    // Enhanced: Fetch dashboard data with feature-based filtering
    const fetchDashboardData = async () => {
      try {
        console.log('🔍 Fetching dashboard data with feature control...');
        
        // ✅ Enhanced: Fetch doctor profile information with picture
        await loadDoctorInfo();
        
        // Enhanced: Fetch appointments only if Care/Report feature is enabled
        if (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Care / Report')) {
          try {
            console.log('🔍 Fetching ALL appointments (feature enabled)...');
            const appointmentsResponse = await doctorService.getAppointments();
            
            let appointments = [];
            if (appointmentsResponse.success && appointmentsResponse.data) {
              appointments = appointmentsResponse.data;
            } else if (Array.isArray(appointmentsResponse)) {
              appointments = appointmentsResponse;
            } else if (appointmentsResponse.data && Array.isArray(appointmentsResponse.data)) {
              appointments = appointmentsResponse.data;
            }
            
            console.log('✅ Fetched appointments:', appointments.length);
            
            // ✅ NEW: Process all appointments with today-first sorting
            const { allSorted, todaysOnly } = processAllAppointments(appointments);
            
            setAllAppointments(allSorted); // Store all sorted appointments
            setTodayAppointments(todaysOnly); // Store just today's for calendar
            
            setStats(prevStats => ({
              ...prevStats,
              totalAppointments: appointments.length
            }));
            
          } catch (appointmentsError) {
            console.error('❌ Error fetching appointments:', appointmentsError);
            setAllAppointments([]);
            setTodayAppointments([]);
          }
        } else {
          console.log('⚠️ Appointments feature disabled - skipping fetch');
          setAllAppointments([]);
          setTodayAppointments([]);
          setStats(prevStats => ({ ...prevStats, totalAppointments: 0 }));
        }
        
        // Enhanced: Fetch patients only if User Profiles feature is enabled
        if (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('User Profiles')) {
          try {
            const patientsResponse = await doctorService.getPatients();
            const patients = patientsResponse.data || patientsResponse.patients || [];
            
            setStats(prevStats => ({
              ...prevStats,
              totalPatients: patients.length
            }));
            
            console.log('✅ Patients loaded:', patients.length);
          } catch (patientsError) {
            console.error('❌ Error fetching patients:', patientsError);
            setStats(prevStats => ({ ...prevStats, totalPatients: 0 }));
          }
        } else {
          console.log('⚠️ User Profiles feature disabled - skipping patients fetch');
          setStats(prevStats => ({ ...prevStats, totalPatients: 0 }));
        }
        
        // Enhanced: Fetch journal entries only if Journal Entries feature is enabled
        if (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Journal Entries')) {
          try {
            const entriesResponse = await doctorService.getJournalEntries({
              analyzed: 'unanalyzed',
              limit: 5
            });
            const entries = entriesResponse.data || [];
            
            setRecentEntries(entries);
            setStats(prevStats => ({
              ...prevStats,
              pendingEntries: entries.length
            }));
            
            console.log('✅ Journal entries loaded:', entries.length);
          } catch (entriesError) {
            console.error('❌ Error fetching journal entries:', entriesError);
            setStats(prevStats => ({ ...prevStats, pendingEntries: 0 }));
          }
        } else {
          console.log('⚠️ Journal Entries feature disabled - skipping fetch');
          setStats(prevStats => ({ ...prevStats, pendingEntries: 0 }));
        }
        
        // Enhanced: Messages only if Notifications feature is enabled
        if (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Notifications')) {
          // Implement message fetching when ready
          setStats(prevStats => ({ ...prevStats, newMessages: 0 }));
        } else {
          setStats(prevStats => ({ ...prevStats, newMessages: 0 }));
        }
        
        setLoading(false);
        console.log('✅ Dashboard data loading complete with feature control');
        
      } catch (error) {
        console.error('❌ Error in fetchDashboardData:', error);
        setLoading(false);
        
        setDoctorInfo({ firstName: 'Doctor', lastName: '' });
        setAllAppointments([]);
        setTodayAppointments([]);
        setStats({
          totalPatients: 0,
          totalAppointments: 0,
          pendingEntries: 0,
          newMessages: 0
        });
      }
    };
    
    // Critical FIX: Only fetch data once when component mounts
    if (featureControl.isFeatureEnabled) {
      fetchDashboardData();
    }
  }, []); // Empty dependency array - runs only once!

  // ✅ Calendar helper functions
  const getMonthName = (date) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[date.getMonth()];
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const goToPreviousPeriod = () => {
    setCalendarState(prevState => {
      const newDate = new Date(prevState.currentDate);
      if (prevState.viewMode === 'week') {
        newDate.setDate(newDate.getDate() - 7);
      } else {
        newDate.setMonth(newDate.getMonth() - 1);
      }
      return { ...prevState, currentDate: newDate };
    });
  };

  const goToNextPeriod = () => {
    setCalendarState(prevState => {
      const newDate = new Date(prevState.currentDate);
      if (prevState.viewMode === 'week') {
        newDate.setDate(newDate.getDate() + 7);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return { ...prevState, currentDate: newDate };
    });
  };

  const toggleViewMode = () => {
    setCalendarState(prevState => ({
      ...prevState,
      viewMode: prevState.viewMode === 'week' ? 'month' : 'week'
    }));
  };

  const getWeekDays = (date) => {
    const day = date.getDay();
    const diff = date.getDate() - day;
    const weekDays = [];
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(date);
      currentDay.setDate(diff + i);
      weekDays.push({
        date: currentDay,
        name: daysOfWeek[i],
        number: currentDay.getDate(),
        month: currentDay.getMonth(),
        year: currentDay.getFullYear()
      });
    }
    return weekDays;
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const totalDays = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const daysArray = [];
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    
    for (let i = firstDay - 1; i >= 0; i--) {
      const dayNumber = prevMonthDays - i;
      daysArray.push({
        date: new Date(year, month - 1, dayNumber),
        name: daysOfWeek[(7 + dayNumber - i) % 7],
        number: dayNumber,
        month: month - 1,
        year: year,
        isCurrentMonth: false
      });
    }
    
    for (let i = 1; i <= totalDays; i++) {
      const dayDate = new Date(year, month, i);
      daysArray.push({
        date: dayDate,
        name: daysOfWeek[dayDate.getDay()],
        number: i,
        month: month,
        year: year,
        isCurrentMonth: true
      });
    }
    
    const remainingDays = 42 - daysArray.length;
    for (let i = 1; i <= remainingDays; i++) {
      const dayDate = new Date(year, month + 1, i);
      daysArray.push({
        date: dayDate,
        name: daysOfWeek[dayDate.getDay()],
        number: i,
        month: month + 1,
        year: year,
        isCurrentMonth: false
      });
    }
    
    return daysArray;
  };

  const hasAppointments = (date) => {
    const dateStr = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    return allAppointments.some(appt => {
      if (!appt.appointmentDate) return false;
      const apptDate = new Date(appt.appointmentDate);
      const apptDateStr = `${apptDate.getFullYear()}-${apptDate.getMonth() + 1}-${apptDate.getDate()}`;
      return apptDateStr === dateStr;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  const renderCalendar = () => {
    const { currentDate, viewMode } = calendarState;
    const isMonthView = viewMode === 'month';
    const days = isMonthView ? getMonthDays(currentDate) : getWeekDays(currentDate);
    const monthYear = `${getMonthName(currentDate)} ${currentDate.getFullYear()}`;
    
    return (
      <DoctorStatusGuard 
        user={user} 
        requiredStatus="approved"
        fallback={
          <div className="weekly-calendar">
            <RestrictedFeatureMessage 
              title="Calendar & Appointments"
              message="Calendar access available after verification"
            />
          </div>
        }
      >
        <FeatureWrapper feature="Care / Report" showMessage={true}>
          <div className="weekly-calendar">
            <div className="calendar-month-header">
              <div className="month-title">{monthYear}</div>
              <div className="month-navigation">
                <button 
                  className="month-nav-button view-toggle" 
                  onClick={toggleViewMode}
                >
                  {isMonthView ? 'Week View' : 'Month View'}
                </button>
                <button className="month-nav-button prev" onClick={goToPreviousPeriod}>&#10094;</button>
                <button className="month-nav-button next" onClick={goToNextPeriod}>&#10095;</button>
              </div>
            </div>
            
            {isMonthView ? (
              <div className="month-grid">
                <div className="day-names-row">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName, index) => (
                    <div key={`header-${index}`} className="day-name-cell">{dayName}</div>
                  ))}
                </div>
                <div className="month-days-grid">
                  {days.map((day, index) => (
                    <div 
                      key={`day-${index}`} 
                      className={`month-day ${!day.isCurrentMonth ? 'other-month' : ''} 
                                  ${isToday(day.date) ? 'today' : ''} 
                                  ${hasAppointments(day.date) ? 'has-appointment' : ''}`}
                    >
                      <div className="month-day-number">{day.number}</div>
                      {hasAppointments(day.date) && (
                        <div className="month-day-indicators">
                          <div className="day-appointment-dot"></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="week-row">
                {days.map((day, index) => (
                  <div 
                    key={`week-day-${index}`} 
                    className={`week-day ${hasAppointments(day.date) ? 'has-appointment' : ''} 
                                ${isToday(day.date) ? 'today' : ''}`}
                  >
                    <div className="week-day-header">
                      <div className="day-name">{day.name}</div>
                      <div className="day-number">{day.number}</div>
                    </div>
                    <div className="day-event-container">
                        {hasAppointments(day.date) && (
                          <>
                            {/* ✅ SHOW ACTUAL APPOINTMENT DETAILS */}
                            {allAppointments
                              .filter(appt => {
                                if (!appt.appointmentDate) return false;
                                const apptDate = new Date(appt.appointmentDate);
                                const dayStr = `${day.date.getFullYear()}-${day.date.getMonth() + 1}-${day.date.getDate()}`;
                                const apptStr = `${apptDate.getFullYear()}-${apptDate.getMonth() + 1}-${apptDate.getDate()}`;
                                return apptStr === dayStr;
                              })
                              .map((appt, index) => (
                                <div key={index} className="appointment-indicator">
                                  <div className="appointment-time">{appt.time}</div>
                                  <div className="appointment-patient">{appt.patientName}</div>
                                </div>
                              ))
                            }
                          </>
                        )}
                      </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </FeatureWrapper>
      </DoctorStatusGuard>
    );
  };

  // ✅ NEW: Appointment Slider Component - Show 1 appointment at a time
  const renderAppointmentSlider = () => {
    // ✅ Get current appointment
    const currentAppointment = allAppointments[currentSlide];
    
    return (
      <>
        <DoctorStatusGuard 
          user={user} 
          requiredStatus="approved"
          fallback={
            <div className="appointment-slider-container">
              <RestrictedFeatureMessage 
                title="Patient Appointments"
                message="Appointment management available after verification"
              />
            </div>
          }
        >
          <FeatureWrapper feature="Care / Report" showMessage={true}>
            <div className="appointment-slider-container">
              <div className="slider-header">
                <h3 className="section-title">
                  Appointments
                  {allAppointments.length > 0 && (
                    <span className="slider-counter">
                      {currentSlide + 1} of {allAppointments.length}
                    </span>
                  )}
                </h3>
                
                {/* ✅ Navigation Arrows */}
                {allAppointments.length > 1 && (
                  <div className="slider-navigation">
                    <button 
                      className="slider-nav-button prev"
                      onClick={goToPrevious}
                    >
                      <span className="nav-arrow">‹</span>
                    </button>
                    <button 
                      className="slider-nav-button next"
                      onClick={goToNext}
                    >
                      <span className="nav-arrow">›</span>
                    </button>
                  </div>
                )}
              </div>
              
              {allAppointments.length === 0 ? (
                <div className="no-appointments-slider">
                  <p>🗓️ No appointments scheduled</p>
                  <p className="no-appointments-subtitle">Create new appointments to get started!</p>
                </div>
              ) : (
                <div className="appointment-slider-content">
                  {/* ✅ Single Appointment Card */}
                  <div className={`appointment-card-slider ${currentAppointment.isToday ? 'is-today' : ''} ${currentAppointment.isPast ? 'is-past' : ''}`}>
                    {/* Status Badge */}
                    <div className={`appointment-status-badge ${getAppointmentStatusBadge(currentAppointment).className}`}>
                      {getAppointmentStatusBadge(currentAppointment).text}
                    </div>
                    
                    {/* Main Content */}
                    <div className="appointment-main-content">
                      {/* Patient Section */}
                      <div className="slider-patient-section">
                        <div className="patient-avatar-large">
                          {currentAppointment.patientName.charAt(0).toUpperCase()}
                        </div>
                        <div className="patient-info-large">
                          <h2 className="patient-name-large">{currentAppointment.patientName}</h2>
                          <p className="patient-type-large">Patient • {currentAppointment.appointmentType}</p>
                        </div>
                      </div>
                      
                      {/* DateTime Section */}
                      <div className="slider-datetime-section">
                        <div className="datetime-row">
                          <div className="datetime-item-large">
                            <span className="datetime-icon-large">📅</span>
                            <div className="datetime-text">
                              <span className="datetime-label">Date</span>
                              <span className="datetime-value-large">
                                {currentAppointment.isToday ? 'Today' : currentAppointment.date}
                              </span>
                            </div>
                          </div>
                          
                          <div className="datetime-item-large">
                            <span className="datetime-icon-large">🕛</span>
                            <div className="datetime-text">
                              <span className="datetime-label">Time</span>
                              <span className="datetime-value-large">{currentAppointment.time}</span>
                            </div>
                          </div>
                          
                          <div className="datetime-item-large">
                            <span className="datetime-icon-large">⏱️</span>
                            <div className="datetime-text">
                              <span className="datetime-label">Duration</span>
                              <span className="datetime-value-large">{currentAppointment.duration} min</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Video Session Indicator */}
                      {currentAppointment.meetingLink && (
                        <div className="video-session-indicator">
                          <span className="video-text">Video Session Available</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="slider-actions">
                      <button 
                        className={`slider-action-button primary ${currentAppointment.isToday ? 'today-session' : ''}`}
                        onClick={() => handleViewDetails(currentAppointment)}
                      >
                        {currentAppointment.isToday && currentAppointment.meetingLink ? 
                          'Join Session' : 
                          'View Details'
                        }
                      </button>
                      
                      {currentAppointment.meetingLink && !currentAppointment.isToday && (
                        <button 
                          className="slider-action-button secondary"
                          onClick={() => handleJoinMeeting(currentAppointment)}
                        >
                          Preview Meeting
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* ✅ Slide Indicators (Dots) */}
                  {allAppointments.length > 1 && (
                    <div className="slide-indicators">
                      {allAppointments.map((_, index) => (
                        <button
                          key={index}
                          className={`slide-dot ${index === currentSlide ? 'active' : ''} ${allAppointments[index].isToday ? 'today-dot' : ''}`}
                          onClick={() => setCurrentSlide(index)}
                          title={`${allAppointments[index].patientName} - ${allAppointments[index].isToday ? 'Today' : allAppointments[index].date}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              
            </div>
          </FeatureWrapper>
        </DoctorStatusGuard>

        {/* ✅ Enhanced: Appointment Details Modal */}
        {showAppointmentModal && selectedAppointment && (
          <div className="modal-overlay-fixed" onClick={closeAppointmentModal}>
            <div className="modal-content-fixed" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="modal-header-fixed">
                <h2 className="modal-title-fixed">Appointment Details</h2>
                <div className={`modal-status-badge ${getAppointmentStatusBadge(selectedAppointment).className}`}>
                  {getAppointmentStatusBadge(selectedAppointment).text}
                </div>
                <button 
                  className="modal-close-fixed"
                  onClick={closeAppointmentModal}
                >
                  ×
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="modal-body-fixed">
                {/* Patient Information */}
                <div className="modal-patient-section">
                  <div className="modal-patient-avatar">
                    {selectedAppointment.patientName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="modal-patient-name">{selectedAppointment.patientName}</h3>
                    <p className="modal-patient-type">Patient • {selectedAppointment.appointmentType}</p>
                  </div>
                </div>
                
                {/* Enhanced: Date & Time Display */}
                <div className="modal-datetime-section">
                  <div className="modal-datetime-item">
                    <span className="datetime-icon">📅</span>
                    <div className="datetime-info">
                      <span className="datetime-label">Date</span>
                      <span className="datetime-value">
                        {selectedAppointment.isToday ? 'Today' : selectedAppointment.date}
                      </span>
                    </div>
                  </div>
                  <div className="modal-datetime-item">
                    <span className="datetime-icon">🕛</span>
                    <div className="datetime-info">
                      <span className="datetime-label">Time</span>
                      <span className="datetime-value">{selectedAppointment.time}</span>
                    </div>
                  </div>
                  <div className="modal-datetime-item">
                    <span className="datetime-icon">⏱️</span>
                    <div className="datetime-info">
                      <span className="datetime-label">Duration</span>
                      <span className="datetime-value">{selectedAppointment.duration} min</span>
                    </div>
                  </div>
                </div>
                
                {/* Meeting Section */}
                <div className="modal-meeting-section">
                  {/* Join Meeting Button or No Meeting Message */}
                  {selectedAppointment.meetingLink ? (
                    <button 
                      className={`modal-join-button ${isSessionActive(selectedAppointment) ? 'active' : ''} ${selectedAppointment.isToday ? 'today-session' : ''}`}
                      onClick={() => handleJoinMeeting(selectedAppointment)}
                    >
                      <span className="join-icon">📹</span>
                      {selectedAppointment.isToday ? 
                        (isSessionActive(selectedAppointment) ? 'Join Meeting Now' : 'Join Today\'s Session') :
                        'Join Video Session'
                      }
                    </button>
                  ) : (
                    <div className="modal-no-meeting">
                      <span className="no-meeting-icon">📹</span>
                      No meeting link available
                    </div>
                  )}
                  
                  {/* Enhanced: Show additional info for today's appointments */}
                  {selectedAppointment.isToday && selectedAppointment.meetingLink && (
                    <div className="today-session-info">
                      <h4>Today's Session Ready</h4>
                      <p>Both you and your patient can join the same video room. The session is available now!</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="modal-footer-fixed">
                <button 
                  className="modal-close-button"
                  onClick={closeAppointmentModal}
                >
                  Close
                </button>
                {selectedAppointment.isToday && selectedAppointment.meetingLink && (
                  <button 
                    className="modal-join-footer-button"
                    onClick={() => handleJoinMeeting(selectedAppointment)}
                  >
                    📹 Join Session
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ✅ Enhanced: StatCard with feature-based styling and conditional rendering
  const StatCard = ({ title, value, subtitle, feature, onClick }) => {
    const isFeatureEnabled = feature ? (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled(feature)) : true;
    
    if (feature && !isFeatureEnabled) {
      return (
        <div className="stat-card disabled" onClick={onClick}>
          <h3 className="stat-title">{title}</h3>
          <div className="stat-value">--</div>
          <div className="stat-subtitle">Feature Disabled</div>
        </div>
      );
    }
    
    return (
      <div className="stat-card" onClick={onClick}>
        <h3 className="stat-title">{title}</h3>
        <div className="stat-value">{value}</div>
        <div className="stat-subtitle">{subtitle}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          Loading {platformName} dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      {/* ✅ NEW: Status Banner for pending doctors */}
      <StatusBanner user={user} />
      
      {/* ✅ Enhanced: Header with tenant branding and profile picture */}
      <div className="dashboard-header">
        <div className="welcome-message">
          <h1 className="welcome-title">
            Welcome back, Dr. {doctorInfo.firstName} {doctorInfo.lastName}.
          </h1>
          <p className="welcome-subtitle">
            {currentTenant?.name ? `${currentTenant.name} - ` : ''}{platformName} Medical Dashboard. 
            Here's a gentle overview to guide your day.
          </p>
        </div>
        <div className="header-actions">
          {/* ✅ NEW: Always accessible profile completion card */}
          <DoctorStatusGuard user={user} requiredStatus="any">
            <div className="quick-access-card">
              <button 
                className="profile-completion-button"
                onClick={() => navigate('/doctor-onboarding')}
                title="Complete your professional profile"
              >
                <span className="button-icon">👤</span>
                Complete Profile
              </button>
            </div>
          </DoctorStatusGuard>
          
          <FeatureWrapper feature="Config">
            <button className="search-button"></button>
          </FeatureWrapper>
          <FeatureWrapper feature="Notifications">
           <button className="notification-button"></button>
          </FeatureWrapper>
          <div className="user-avatar">
            <div className={`avatar-placeholder ${profileLoading ? 'loading' : ''}`}>
              {getDoctorProfileDisplay()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Divider above Patient and Appointment Overview */}
      <div className="section-divider overview-divider"></div>

      {/* ✅ Enhanced: Overview section with feature-based stats and status guard */}
      <div className="overview-section">
        <div className="section-header">
          <h2 className="section-title">
            Patient and Appointment Overview
          </h2>
          <div className="timeframe-selector">
            <select 
              value={selectedTimeframe} 
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="timeframe-select"
            >
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="This Year">This Year</option>
            </select>
          </div>
        </div>
        
        <div className="stats-container">
          {/* ✅ RESTRICTED: Total Patients */}
          <DoctorStatusGuard 
            user={user} 
            requiredStatus="approved"
            fallback={
              <div className="stat-card disabled">
                <h3 className="stat-title">Total Patients</h3>
                <div className="stat-value">🔒</div>
                <div className="stat-subtitle">Available After Verification</div>
              </div>
            }
          >
            <StatCard
              title="Total Patients"
              value={stats.totalPatients}
              subtitle="Patients in Your Care"
              feature="User Profiles"
              onClick={() => featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('User Profiles') && navigate('/doctor/patients')}
            />
          </DoctorStatusGuard>

          {/* ✅ RESTRICTED: Total Appointments */}
          <DoctorStatusGuard 
            user={user} 
            requiredStatus="approved"
            fallback={
              <div className="stat-card disabled">
                <h3 className="stat-title">Total Appointments</h3>
                <div className="stat-value">🔒</div>
                <div className="stat-subtitle">Available After Verification</div>
              </div>
            }
          >
            <StatCard
              title="Total Appointments"
              value={stats.totalAppointments}
              subtitle="All Scheduled Sessions"
              feature="Care / Report"
              onClick={() => featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Care / Report') && navigate('/doctor/appointments')}
            />
          </DoctorStatusGuard>

          {/* ✅ RESTRICTED: Pending Review */}
          <DoctorStatusGuard 
            user={user} 
            requiredStatus="approved"
            fallback={
              <div className="stat-card disabled">
                <h3 className="stat-title">Pending Review</h3>
                <div className="stat-value">🔒</div>
                <div className="stat-subtitle">Available After Verification</div>
              </div>
            }
          >
            <StatCard
              title="Pending Review"
              value={stats.pendingEntries}
              subtitle="Journal Entries to Review"
              feature="Journal Entries"
              onClick={() => featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Journal Entries') && navigate('/doctor/journal-entries')}
            />
          </DoctorStatusGuard>

          {/* ✅ RESTRICTED: Messages */}
          <DoctorStatusGuard 
            user={user} 
            requiredStatus="approved"
            fallback={
              <div className="stat-card disabled">
                <h3 className="stat-title">Messages</h3>
                <div className="stat-value">🔒</div>
                <div className="stat-subtitle">Available After Verification</div>
              </div>
            }
          >
            <StatCard
              title="Messages"
              value={stats.newMessages}
              subtitle="New Messages"
              feature="Notifications"
              onClick={() => featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Notifications') && navigate('/doctor/messages')}
            />
          </DoctorStatusGuard>
        </div>
      </div>
      
      {/* Divider above calendar and appointments */}
      <div className="section-divider calendar-divider"></div>

      {/* ✅ Enhanced: Main content with feature wrappers and status guards */}
      <div className="main-content">
        <div className="calendar-section">
          {renderCalendar()}
        </div>
        <div className="appointments-section">
          {renderAppointmentSlider()}
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;